import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackScrollDepth } from '@/lib/analytics';

const THRESHOLDS = [25, 50, 75, 100];

/**
 * Fires a GA4 `scroll_depth` event once per threshold per page view.
 * Resets when the route changes. Mounted once globally in App, so it covers
 * every page automatically.
 */
export default function ScrollDepthTracker() {
  const { pathname, search } = useLocation();
  const firedRef = useRef(new Set());
  const pageKey = useRef('');

  useEffect(() => {
    const fullPath = pathname + (search || '');
    pageKey.current = fullPath;
    firedRef.current = new Set();
  }, [pathname, search]);

  useEffect(() => {
    let ticking = false;

    const compute = () => {
      ticking = false;
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const total = (doc.scrollHeight - window.innerHeight);
      if (total <= 0) return; // page not scrollable
      const percent = Math.min(100, Math.round((scrollTop / total) * 100));
      THRESHOLDS.forEach((t) => {
        if (percent >= t && !firedRef.current.has(t)) {
          firedRef.current.add(t);
          trackScrollDepth(t, pageKey.current);
        }
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname, search]);

  return null;
}